<script lang="ts">
  import { getMediaQuery } from "app/utils/mediaQuery";
  import cx from "classnames";
  import { onMount } from "svelte";
  import HeadingScrollSpy from "./HeadingScrollSpy.svelte";
  import { NodeViewContent, NodeViewWrapper } from "svelte-tiptap";
  import OVIcon from "app/components/+shared/OVIcon/OVIcon.svelte";
  import DialogueModal from "$components/+shared/Modal/DialogueModal.svelte";

  import CrossIconButton from "app/components/+shared/CrossIconButton.svelte";
  import { AlignLeft } from "$components/+shared/OVIcon";
  import { community } from "app/stores/community";
  import { tooltip } from "app/actions/tooltip";
  import type { Editor } from "@tiptap/core";

  const INDENT_STYLE = ["pl-5", "pl-7", "pl-9", "pl-11", "pl-13"];

  export let editor: Editor;
  export let isEditable: boolean;
  $$restProps;

  let confirmRemoveOpen = false;

  interface Heading {
    level: string;
    id: string;
    text: string;
  }

  /**
   * Render a table of contents list aside the main content body based on headings.
   *
   */
  let headings: Heading[] = [];
  const isLargeDesktop = getMediaQuery("large-desktop");

  const updateContentsTable = () => {
    const _headings: Heading[] = [];
    // collect each heading and add unique dom id to reference later
    editor.state.doc.descendants((node) => {
      if (node.type.name === "heading") {
        _headings.push({
          level: node.attrs.level,
          text: node.textContent,
          id: node.attrs.id,
        });
      }
    });
    headings = _headings;
  };

  // effectively useHashLinkScroll - scrolls to hashlink after load
  onMount(() => {
    const { hash } = window.location;
    if (!hash) return;

    // Push onto callback queue so it runs after the DOM is updated,
    // this is required when navigating from a different page so that
    // the element is rendered on the page before trying to getElementById.
    requestAnimationFrame(() => {
      setTimeout(() => {
        const element = document.getElementById(hash.replace("#", ""));
        if (element) element.scrollIntoView();
      }, 0);
    });
  });

  onMount(() => {
    if (!editor) return;

    // update contents for readonly docs once
    if (!isEditable) {
      editor.on("create", updateContentsTable);
      return () => {
        editor.off("create", updateContentsTable);
      };
    }

    // update contents for editable docs on every editor update
    editor.on("update", updateContentsTable);

    return () => {
      editor.off("update", updateContentsTable);
    };
  });
</script>

<NodeViewWrapper
  class={cx(
    "gap-4",
    // 2 columns with flexible widths in a grid container.
    // The first column has a minimum width of 30ch and a maximum width of 800px.
    // The second column has a minimum width of its maximum content width and a maximum width of 1 fraction unit of the available space.
    // The aim is to keep the main content column as wide as possible, but not wider than 800px, fixed in the middle of the page,
    // while making sure the ToC-aside is fixed on the right side of the page, narrow enough to fit the max-content.
    "relative grid grid-cols-1 transition-[width] @4xl/main-panel-container:grid-cols-[minmax(30ch,_800px)_minmax(max-content,_1fr)]"
  )}
>
  <!-- { Main content rendered here } -->
  <NodeViewContent class="content-block-wrapper content w-full" />

  <!-- { ToC-aside } -->
  {#if $isLargeDesktop}
    <div
      id="toc"
      class="hover:scrollbar-visible scrollbar-invisible scrollbar-thin sticky top-0 hidden h-fit max-h-[90vh] w-fit min-w-[250px] overflow-x-hidden overflow-y-scroll pl-10 pr-1 caret-black @4xl/main-panel-container:block"
      contentEditable={false}
    >
      <div class="flex items-center justify-between">
        <div class="py-2 text-xs font-medium opacity-70">
          <OVIcon icon={AlignLeft} class="inline-block pr-1" />
          In this doc
        </div>
        {#if isEditable}
          <CrossIconButton
            position="top-0 right-2"
            a11yLabel="Remove contents"
            on:click={() => {
              confirmRemoveOpen = true;
            }}
          />
        {/if}
      </div>

      <HeadingScrollSpy let:currentHeading {editor}>
        <!-- parent passes in the heading currently in view to slot prop -->
        <ul class="not-prose sticky list-none px-0 text-xs">
          {#each headings as heading}
            {@const isActive = currentHeading === heading.id}
            <li
              style={isActive ? `border-color: ${$community.primaryColor}` : undefined}
              class="{INDENT_STYLE[
                Number(heading.level) - 1
              ]} transition-color h-full w-full max-w-[26ch] truncate border-l border-black/5 py-1 dark:border-white/5"
            >
              <a
                style={isActive ? `color: ${$community.primaryColor}` : undefined}
                class="h-full py-1 no-underline hover:text-gray-900 dark:hover:text-gray-100"
                class:font-normal={!isActive}
                class:dark:text-gray-400={!isActive}
                class:font-semibold={isActive}
                class:dark:text-white={isActive}
                href="#{heading.id}"
                title={heading.text}
                use:tooltip
              >
                {heading.text}
              </a>
            </li>
          {/each}
        </ul>
      </HeadingScrollSpy>
    </div>
  {/if}
</NodeViewWrapper>

{#if confirmRemoveOpen}
  <DialogueModal
    bind:open={confirmRemoveOpen}
    title="Are you sure you want to remove doc contents block?"
    message="This will remove the contents block from the document. You can add it back later via the '/' formatting menu."
    on:confirm={() => {
      confirmRemoveOpen = false;
      editor.chain().selectAll().toggleWrap("tableOfContents").setTextSelection(0).run();
    }}
    on:close={() => (confirmRemoveOpen = false)}
    danger
    confirmLabel="Remove contents block"
  ></DialogueModal>
{/if}
